import { useNavigate, useParams } from 'react-router-dom';

import { SubsetForm } from '../../components/subset-form';
import { useGetCase } from '@/api/queries/useGetCase';
import { useGetDocIds } from '@/api/queries/useGetDocIds';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { Icon } from '@/new/components/icon';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';

export function SubsetEditor() {
  const { matterId, subsetId } = useParams();
  const navigate = useNavigate();

  if (!matterId || !subsetId) {
    return null;
  }

  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset, isLoading: isLoadingSubset } = useGetCase(subsetId);
  const { data: docIds, isLoading: isLoadingDocs } = useGetDocIds(subsetId);

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Edit', path: 'edit' },
  });

  // Navigate back to subset overview after successful update
  const handleSuccess = (updatedSubsetId: string) => {
    navigate(`/matters/${matterId}/subsets/${updatedSubsetId}`);
  };

  const isLoading = isLoadingSubset || isLoadingDocs;

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div className="flex h-full flex-col">
        <Breadcrumb items={breadcrumbItems} />

        <div className="flex h-full items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Icon className="lucide--loader-circle h-8 w-8 animate-spin text-gray-400 duration-700" />
            <p className="text-gray-500">Loading subset data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if data is missing
  if (!subset || !docIds) {
    return (
      <div className="flex h-full flex-col">
        <Breadcrumb items={breadcrumbItems} />

        <div className="flex h-full items-center justify-center">
          <div className="flex flex-col items-center gap-4 text-center">
            <Icon className="lucide--alert-circle h-12 w-12 text-red-500" />
            <div>
              <h3 className="text-lg font-medium text-gray-900">Failed to load subset</h3>
              <p className="mt-2 text-gray-500">Unable to retrieve subset data. Please try again.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Transform data into initialData format for SubsetForm
  const initialData = {
    name: subset.name,
    legalIssues: subset.legalIssues ?? '',
    selectedDocIds: docIds.documents.map((doc) => doc.id),
  };

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb items={breadcrumbItems} />

      <SubsetForm
        mode="edit"
        matterId={matterId}
        subsetId={subsetId}
        initialData={initialData}
        onSuccess={handleSuccess}
        matterIssues={matter?.issues}
      />
    </div>
  );
}
